import { LLMResponseEntity } from "@swivl-mono/entities";
import { Conversation, PromptInterface, PromptType, ReviewInterface, SearchLLMResponse, StructuredLLMResponse } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK - /apps/server/src/API/AI/AI.Controller.ts
export class AIAPI {
  static async respondWithLLMToBrain(orgId: number, model: string, prompt: string, numberOfTokensInResponse: number, transcript: string) {
    return APIService.Server.get<string>(`/ai/org/${orgId}/llm-brain`, { model, prompt, numberOfTokensInResponse, transcript });
  }

  static async getPrompt(orgId: number, promptSlug: string) {
    return APIService.Server.get<PromptInterface>(`/ai/org/${orgId}/prompt-slug/${promptSlug}`);
  }

  static async getPromptResponse(orgId: number, model: string, prompt: string, numberOfTokensInResponse: number) {
    return APIService.Server.get<string>(`/ai/org/${orgId}/get-response`, { model, prompt, numberOfTokensInResponse });
  }
  static async saveLLMResponse(
    orgId: number,
    options: {
      questId: number;
      userId: number;
      questSlug: string;
      prompt: string;
      response: string;
      promptChain?: any;
      message?: any;
      // Enhanced logging options for Phase 6
      mode?: "text" | "structured" | "search";
      requestPayload?: object;
      responsePayload?: object;
      requestHeaders?: Record<string, string>;
      requestUrl?: string;
      promptId?: number;
      promptSlug?: string;
      originalSchema?: object;
      processedSchema?: object;
      variableMappings?: object;
      variablesStored?: string[];
      success?: boolean;
      error?: any;
      isOpenAIError?: boolean;
      startTime?: number;
      requestTimestamp?: string;
      responseTimestamp?: string;
    },
  ) {
    return APIService.Server.put<LLMResponseEntity>(`/ai/org/${orgId}/llm-response`, options);
  }

  static async predictAutoCompleteText(orgId: number, transcript: string) {
    return APIService.Server.get<string>(`/ai/org/${orgId}/predict-autocomplete`, { transcript });
  }
  static async generateReviewReply(orgId: number, review: string, rating: number) {
    return APIService.Server.get<string>(`/ai/org/${orgId}/generate-review-reply`, { review: review, rating: rating });
  }
  static async getAllPrompts(orgId: number) {
    return APIService.Server.get<PromptInterface[]>(`/ai/org/${orgId}/prompts`);
  }
  static async savePrompt(orgId: number, prompt: Partial<PromptInterface>) {
    return APIService.Server.put<PromptInterface>(`/ai/org/${orgId}/prompt`, prompt);
  }
  static async deletePrompt(orgId: number, slug: string) {
    return APIService.Server.delete<boolean>(`/ai/org/${orgId}/prompt/${slug}`);
  }

  static async generateReviewSummary(
    orgId: number,
    reviews: ReviewInterface[],
    locationNames: { [key: number]: string },
    startDate?: string,
    endDate?: string,
  ) {
    // Send only essential fields to avoid TSOA validation errors
    const cleanedReviews = reviews.map((review) => ({
      locationId: review.locationId,
      rating: review.rating,
      text: review.text,
      reviewerName: review.reviewerName,
      reviewDate: review.reviewDate,
    }));

    // Convert number keys to strings for TSOA compatibility
    const stringKeyLocationNames: { [key: string]: string } = {};
    Object.entries(locationNames).forEach(([key, value]) => {
      stringKeyLocationNames[key] = value;
    });

    return APIService.Server.post<string>(`/ai/org/${orgId}/generate-review-summary`, {
      reviews: cleanedReviews,
      locationNames: stringKeyLocationNames,
      startDate,
      endDate,
    });
  }
  static async getPromptsByType(orgId: number, type: PromptType) {
    return APIService.Server.get<PromptInterface[]>(`/ai/org/${orgId}/prompts-by-type/${type}`);
  }

  static async executeStructuredLLM(orgId: number, promptId: number, userId: number) {
    return APIService.Server.get<StructuredLLMResponse>(`/ai/org/${orgId}/llm-structured/user/${userId}/prompt/${promptId}`);
  }

  static async executeSearchLLM(orgId: number, promptId: number, userId: number) {
    return APIService.Server.get<SearchLLMResponse>(`/ai/org/${orgId}/llm-search/user/${userId}/prompt/${promptId}`);
  }


  static async customizeWorksheetMessages(
    orgId: number,
    worksheetName: string,
    faqContent: string,
    worksheetConfig: any
  ) {
    return APIService.Server.post<{ success: boolean; response?: any; error?: string }>(`/ai/customize-worksheet`, {
      orgId,
      worksheetName,
      faqContent,
      worksheetConfig
    });
  }
}
